/**
 * BillingPage — hosts the BetaPassCheckout. Auth-gated; redirects to login
 * if the user isn't signed in.
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { GitBranch, Layers, MessageSquare } from 'lucide-react';
import BetaPassCheckout from './BetaPassCheckout';
import { useAuth } from '../context/AuthContext';

export default function BillingPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login?next=/billing');
    }
  }, [loading, user, navigate]);

  if (loading || !user) return null;

  return (
    <div className="min-h-screen text-white pb-16" style={{ background: '#0A0A0A' }} data-testid="billing-page">
      <div className="max-w-3xl mx-auto px-4 md:px-6 pt-10">
        <div className="font-hud text-[10px] tracking-widest uppercase text-[#D4A84A] mb-2">Beta Pass</div>
        <h1 className="font-historical text-3xl md:text-5xl text-white font-light leading-tight">
          Walk every chain. Earn the certs. Submit the forks.
        </h1>
        <p className="font-mono text-sm md:text-base text-white/70 mt-3 max-w-2xl">
          The Beta Pass unlocks the methodology in full — every locked chain, every era certificate
          pathway, the fork-and-submit loop, and priority queueing on the AI orchestrator's demand
          board. We built it for the teachers, parents, and curious minds walking the first chain
          tonight.
        </p>

        <div className="mt-8">
          <BetaPassCheckout />
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 font-mono text-[11px] text-white/65">
          <div className="border border-white/10 rounded p-4">
            <div className="flex items-center gap-2 text-[#D4A84A] mb-1.5">
              <Layers size={12} /> 9 Era Certificates
            </div>
            Every era walkable. Earn a certificate per era. Earn the Master cert by walking all nine.
          </div>
          <div className="border border-white/10 rounded p-4">
            <div className="flex items-center gap-2 text-[#D4A84A] mb-1.5">
              <GitBranch size={12} /> Fork & Submit
            </div>
            Disagree with a step? Fork the chain, modify it with better sources, submit for review.
          </div>
          <div className="border border-white/10 rounded p-4">
            <div className="flex items-center gap-2 text-[#D4A84A] mb-1.5">
              <MessageSquare size={12} /> Librarian AI
            </div>
            Socratic mentor for stuck moments. Asks questions. Doesn't give answers.
          </div>
        </div>
      </div>
    </div>
  );
}
